import type { FormikErrors } from "formik"
import React, { type ReactNode, createContext, useState } from "react"

import { IAddress } from "models"

import type { AddressVerificationInfo } from "../hooks"

interface SmartyAddressVerificationContextProps {
  isOverlayVisible: boolean
  showAutoCompleteOverlay: () => void
  hideAutoCompleteOverlay: () => void
  addressVerificationInfo?: AddressVerificationInfo
  updateAddressVerificationInfo: React.Dispatch<React.SetStateAction<AddressVerificationInfo | undefined>>
  autoCompleteRecommendedAddress?: (
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<void | FormikErrors<IAddress>>,
    parentFieldName?: string,
  ) => void
  bypassAddressValidation?: () => void
}

const SmartyAddressVerificationContext = createContext<SmartyAddressVerificationContextProps | undefined>(undefined)
SmartyAddressVerificationContext.displayName = "SmartyAddressVerificationContext"

const SmartyAddressVerificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const [addressVerificationInfo, setAddressVerificationInfo] = useState<AddressVerificationInfo>()

  const showAutoCompleteOverlay = () => setIsOverlayVisible(true)
  const hideAutoCompleteOverlay = () => setIsOverlayVisible(false)

  const autoCompleteRecommendedAddress = (
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<void | FormikErrors<IAddress>>,
    parentFieldName = "",
  ) => {
    if (addressVerificationInfo?.recommendedAddress?.address) {
      const {
        recommendedAddress: { address: recommendedAddressData },
      } = addressVerificationInfo

      const fieldPrefix = parentFieldName ? `${parentFieldName}.` : "address."

      Object.entries(recommendedAddressData).forEach(([key, value]) => {
        setFieldValue(`${fieldPrefix}${key}`, value)
      })

      if (addressVerificationInfo.shouldPromptAutoCompleteOnUse) {
        showAutoCompleteOverlay()
      }
    }
  }

  const bypassAddressValidation = () => {
    setAddressVerificationInfo((prev) => ({
      ...prev,
      shouldBypassValidation: !addressVerificationInfo?.shouldBypassValidation,
    }))
  }

  return (
    <SmartyAddressVerificationContext.Provider
      value={{
        isOverlayVisible,
        showAutoCompleteOverlay,
        hideAutoCompleteOverlay,
        addressVerificationInfo,
        updateAddressVerificationInfo: setAddressVerificationInfo,
        autoCompleteRecommendedAddress,
        bypassAddressValidation,
      }}
    >
      {children}
    </SmartyAddressVerificationContext.Provider>
  )
}

export { SmartyAddressVerificationContext, SmartyAddressVerificationProvider }
